import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CornerDownLeft, Layers, FileText, Compass } from 'lucide-react';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';

const staticPages = (t) => [
  { label: t('search.pages.materials'), path: '/materials', kind: 'page' },
  { label: t('search.pages.grades'), path: '/grades', kind: 'page' },
  { label: t('search.pages.compare'), path: '/compare', kind: 'page' },
  { label: t('search.pages.calculator'), path: '/tools/steel-weight-calculator', kind: 'page' },
  { label: t('search.pages.insights'), path: '/insights', kind: 'page' },
  { label: t('search.pages.projects'), path: '/projects', kind: 'page' },
  { label: t('search.pages.certifications'), path: '/certifications', kind: 'page' },
  { label: t('search.pages.careers'), path: '/careers', kind: 'page' },
  { label: t('search.pages.contact'), path: '/contact', kind: 'page' },
];

const ICONS = { grade: Layers, article: FileText, page: Compass };

export default function SearchPalette() {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState([]);
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('axicor:open-search', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('axicor:open-search', onOpen);
    };
  }, []);

  const loadIndex = useCallback(async () => {
    if (index.length > 0) return;
    const entries = [...staticPages(t)];
    try {
      const [grades, articles] = await Promise.all([
        api.get('/catalog/grades'),
        api.get('/insights'),
      ]);
      grades.data.forEach(g => entries.push({
        label: g.name, path: `/materials/${g.category_slug}/${g.slug}`, kind: 'grade',
        hint: [g.brand, ...(Object.values(g.designations || {})), ...(g.industries || [])].filter(Boolean).join(' '),
      }));
      articles.data.forEach(a => entries.push({
        label: a.title, path: `/insights/${a.slug}`, kind: 'article', hint: a.dek,
      }));
    } catch { /* offline: static pages still searchable */ }
    setIndex(entries);
  }, [index.length, t]);

  useEffect(() => { setIndex([]); }, [lang]);

  useEffect(() => { if (open) loadIndex(); }, [open, loadIndex]);

  const results = useMemo(() => {
    if (!query.trim()) return index.slice(0, 8);
    const q = query.toLowerCase();
    return index
      .filter(e => e.label.toLowerCase().includes(q) || (e.hint || '').toLowerCase().includes(q))
      .slice(0, 12);
  }, [query, index]);

  useEffect(() => setCursor(0), [query]);

  const logged = useRef('');
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2 || logged.current === term.toLowerCase()) return;
    const id = setTimeout(() => {
      logged.current = term.toLowerCase();
      api.post('/analytics/search', { q: term, source: 'palette', results: results.length, lang }).catch(() => {});
    }, 800);
    return () => clearTimeout(id);
  }, [query, results.length, lang]);

  const go = (item) => {
    const term = query.trim();
    if (term.length >= 2) {
      api.post('/analytics/search', { q: term, source: 'palette', results: results.length, lang, clicked: item.slug || item.path })
        .catch(() => {});
    }
    setOpen(false);
    setQuery('');
    navigate(item.path);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center pt-[12vh] px-4" data-testid="search-palette">
      <button aria-label="Close search" onClick={() => setOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-xl bg-white shadow-2xl">
        <div className="flex items-center gap-3 border-b border-gray-200 px-5">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            autoFocus
            data-testid="search-palette-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, results.length - 1)); }
              if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
              if (e.key === 'Enter' && results[cursor]) go(results[cursor]);
            }}
            placeholder={t('search.placeholder')}
            className="flex-1 py-4 text-sm focus:outline-none"
          />
          <kbd className="text-[10px] font-mono text-gray-400 border border-gray-200 px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-[50vh] overflow-y-auto" data-testid="search-palette-results">
          {results.map((r, i) => {
            const Icon = ICONS[r.kind] || Compass;
            return (
              <button
                key={r.path + r.label}
                onMouseEnter={() => setCursor(i)}
                onClick={() => go(r)}
                data-testid={`search-result-${i}`}
                className={`w-full text-left px-5 py-3 flex items-center gap-3 transition-colors ${
                  cursor === i ? 'bg-mist' : 'bg-white'
                }`}
              >
                <Icon className="w-4 h-4 text-blue-brand shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-ink truncate">{r.label}</span>
                  {r.hint && <span className="block text-xs text-gray-400 truncate">{r.hint}</span>}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-gray-400">{t(`search.kinds.${r.kind}`)}</span>
                {cursor === i && <CornerDownLeft className="w-3.5 h-3.5 text-gray-400" />}
              </button>
            );
          })}
          {results.length === 0 && (
            <p className="px-5 py-8 text-sm text-gray-400 text-center">{t('search.nothing')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
